import * as React from 'react';
import {useSelector} from 'react-redux';
import {Button} from 'src/designSystem2021Components/buttons';
import {Clickable, Smallest} from 'src/designSystem2021Components/text-v2';
import {Input} from 'src/components/agency-onboard/lib/input/input';
import {Collapsible} from 'src/designSystem2021Components/collapsible/collapsible';
import {Textarea} from 'src/designSystem2021Components/textarea';
import {DefaultFooter} from 'src/components/agency-onboard/footer/default-footer';
import {Select} from 'src/designSystem2021Components/select';
import {selectAgencyData} from 'src/selectors/agency';
import {InfoLabel} from 'src/components/agency-onboard/info-label/info-label';
import classify from 'src/utils/classify';
import {AnalyticsService} from 'src/analytics';
import {AMPLITUDE_EVENTS} from 'src/components/agency-onboard/amplitude_event';

import type {Task} from 'src/components/agency-onboard/types/side-panel-comp';

import {
  MouseTipCompanyDetails,
  MouseTipBusinessReg,
} from 'src/components/agency-onboard/constants';
import maincss from 'src/components/agency-onboard/agency-onboard.css';
import css from 'src/components/agency-onboard/side-panels/tendlc-registration.css';
import designcss from 'src/components/design-doc.css';
import PlusIcon from 'src/images/icons/plus2.svg?noAttrs';
import DeleteIcon from 'src/images/icons/delete-icon.svg';


type SelectOption = {
  label: string;
  value: string;
};

type RepDetailForm = {
  full_name: string;
  email: string;
  phone_number: string;
  title: string;
};

type TenDLCFormData = {
  legal_business_name: string;
  dba: string;
  company_physical_address: string;
  company_type: SelectOption;
  business_registration_number: string;
  base_sense_url: string;
  duns_or_gin_or_lei: string;
  company_website: string;
  region: SelectOption;
  stock_symbol: string;
  stock_exchange: string;
  repList: Array<RepDetailForm>;
};

export type TenDLCFormProps = {
  task: Task;
  onTaskSubmit: (id: string, apiData: unknown) => unknown;
  onTaskCancel: () => unknown;
  data: TenDLCFormData;
};

export const TenDLCRegistrationSidePanel = (props: TenDLCFormProps) => {
  const {task, onTaskSubmit, onTaskCancel, data} = props;
  const agencyData = useSelector(selectAgencyData);

  // variables
  const repDetail = {
    full_name: '',
    email: '',
    phone_number: '',
    title: '',
  } as const;

  const reqFields = [
    'legal_business_name',
    'company_physical_address',
    'business_registration_number',
    'base_sense_url',
    'company_website',
  ];

  // constants
  const ALLOWED_REPS_NUMBER = 2;

  const selectFieldOptions = {
    company_type: [
      {label: 'Private Company', value: 'Private Company'},
      {label: 'Publicly Traded Company', value: 'Publicaly Traded Company'},
      {label: 'Charity/Non-Profit', value: 'Charity/Non-Profit'},
    ],
    region: [
      {label: 'USA and Canada', value: 'USA and Canada'},
      {label: 'Europe', value: 'Europe'},
      {label: 'Asia', value: 'Asia'},
      {label: 'Africa', value: 'Africa'},
      {label: 'Latin America', value: 'Latin America'},
    ],
  } as const;

  const handleTrackingEvent = (event: string) => {
    AnalyticsService.track(event, {
      agencyId: agencyData.id,
      agencyName: agencyData.name,
    });
  };

  const getCurrentData = (data: TenDLCFormData) => {
    if (!Object.keys(data).length) {
      return {};
    }

    const selectFields = ['company_type', 'region'];
    const ignoreFields = ['repList'];
    const formData: Record<string, any> = {};

    Object.keys(data).forEach((key) => {
      if (ignoreFields.includes(key)) {
        return;
      } else if (selectFields.includes(key)) {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly company_type: readonly [{ readonly label: "Private Company"; readonly value: "Private Company"; }, { readonly label: "Publicly Traded Company"; readonly value: "Publicaly Traded Company"; }, { ...; }]; readonly region: readonly [...]; }'. | TS7006 - Parameter 'option' implicitly has an 'any' type.
        selectFieldOptions[key].forEach((option) => {
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'TenDLCFormData'.
          if (option['value'] === data[key]) {
            formData[key] = option;
          }
        });
      } else {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'TenDLCFormData'.
        formData[key] = data[key];
      }
    });

    return formData;
  };

  const [repList, setRepList] = React.useState(
    data.repList || [{...repDetail}],
  );
  const [formData, setFormData] = React.useState<TenDLCFormData>(
    // @ts-expect-error - TS2345 - Argument of type 'Record<string, any>' is not assignable to parameter of type 'TenDLCFormData | (() => TenDLCFormData)'.
    getCurrentData(data),
  );
  const [addRepDisabled, setAddRepDisabled] = React.useState(false);
  const [disableSubmit, setDisableSubmit] = React.useState(true);
  const [isInputDisabled, setInputDisabled] = React.useState(false);

  React.useEffect(() => {
    setDisableSubmit(!checkIfFormComplete());
    setInputDisabled(task['status'] !== 'Pending');
  }, [formData, repList, task]);

  const handleFormDataChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const curFormData = {...formData} as const;
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly legal_business_name: string; readonly dba: string; readonly company_physical_address: string; readonly company_type: SelectOption; readonly business_registration_number: string; ... 6 more ...; readonly repList: RepDetailForm[]; }'.
    curFormData[event.target.name] = event.target.value;
    setFormData(curFormData);
  };

  const handleSelectChange = (
    field: string,
    option:
      | SelectOption
      | null
      | undefined
      | {
          label: string;
          value: string;
        },
  ) => {
    const curFormData = {...formData} as const;
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly legal_business_name: string; readonly dba: string; readonly company_physical_address: string; readonly company_type: SelectOption; readonly business_registration_number: string; ... 6 more ...; readonly repList: RepDetailForm[]; }'.
    curFormData[field] = option;
    setFormData(curFormData);
  };

  const handleAddRep = () => {
    // only two reps are allowed
    if (repList.length >= ALLOWED_REPS_NUMBER - 1) {
      setAddRepDisabled(true);
    }

    const newRep = {...repDetail} as const;
    setRepList([...repList, newRep]);
    handleTrackingEvent(AMPLITUDE_EVENTS.ADD_REP_CLICKED);
  };

  const handleDeleteRep = (index: number) => {
    const reps = [...repList];
    reps.splice(index, 1);
    if (!reps.length) {
      const newRep = {...repDetail} as const;
      reps.push(newRep);
    }

    setRepList(reps);
    setAddRepDisabled(false);
  };

  const handleRepDetailChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const curData = [...repList];
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'RepDetailForm'.
    curData[index][event.target.name] = event.target.value;
    setRepList(curData);
  };

  const handleFormSubmit = () => {
    // select elements have label and value so need to loop through it
    const userData: Record<string, any> = {};
    Object.keys(formData).forEach((key) => {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'TenDLCFormData'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'TenDLCFormData'.
      userData[key] = formData[key].value || formData[key];
    });

    userData['repList'] = [...repList];
    if (repList.length === 2) {
      handleTrackingEvent(AMPLITUDE_EVENTS.SUBMIT_BUTTON_CLICKED_WITH_TWO_REPS);
    }
    onTaskSubmit(task['id'], userData);
    handleTrackingEvent(AMPLITUDE_EVENTS.TEN_DLC_SUBMIT_BUTTON_CLICKED);
  };

  const handleFormCancel = () => {
    onTaskCancel();
  };

  const checkIfFormComplete = () => {
    let isFormComplete = true;
    reqFields.forEach((field) => {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'TenDLCFormData'.
      if (!formData[field]) {
        isFormComplete = false;
      }
    });

    repList.forEach((rep) => {
      Object.keys(repDetail).forEach((key) => {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'RepDetailForm'.
        if (!rep[key]) {
          isFormComplete = false;
        }
      });
    });
    return isFormComplete;
  };

  return (
    <div className={maincss.sidePanel}>
      <Collapsible
        title={
          <InfoLabel
            text="Enter Company Details"
            content={MouseTipCompanyDetails}
            fixedTo="bottom"
            infoBoxClass={css.infoBox}
            infoIconClass={maincss.infoIcon}
          ></InfoLabel>
        }
        containerClassName={designcss.collapsibleBlock}
        headerClassName={maincss.headerText}
        isOpen
      >
        <div className={maincss.sidePanelForm}>
          <div className={maincss.inputBox}>
            <Input
              name="legal_business_name"
              value={formData.legal_business_name || ''}
              disabled={isInputDisabled}
              type="text"
              label="Legal Business Name*"
              boxClassName={maincss.outerBoxInputField}
              onChange={(event) => handleFormDataChange(event)}
            />
          </div>
          <div className={maincss.inputBox}>
            <Input
              name="dba"
              value={formData.dba || ''}
              disabled={isInputDisabled}
              type="text"
              label="DBA (if any)"
              boxClassName={maincss.outerBoxInputField}
              onChange={(event) => handleFormDataChange(event)}
            />
          </div>
          <div className={css.textArea}>
            <Textarea
              name="company_physical_address"
              value={formData.company_physical_address || ''}
              disabled={isInputDisabled}
              rows="7"
              label={
                <Smallest className={maincss.inputLabel}>
                  Company Physical Address*
                </Smallest>
              }
              boxClassName={maincss.outerBoxTextAreaField}
              onChange={(event) => handleFormDataChange(event)}
            />
          </div>
          <div className={maincss.inputBox}>
            <Select
              name="company_type"
              value={formData.company_type || null}
              disabled={isInputDisabled}
              // @ts-expect-error - TS4104 - The type 'readonly [{ readonly label: "Private Company"; readonly value: "Private Company"; }, { readonly label: "Publicly Traded Company"; readonly value: "Publicaly Traded Company"; }, { readonly label: "Charity/Non-Profit"; readonly value: "Charity/Non-Profit"; }]' is 'readonly' and cannot be assigned to the mutable type 'SelectOption[]'.
              options={selectFieldOptions['company_type']}
              classNames={{box: maincss.outerBoxInputField}}
              onChange={(option) => handleSelectChange('company_type', option)}
              label="Company Type"
            />
          </div>
          <div className={maincss.inputBox}>
            <Input
              name="business_registration_number"
              value={formData.business_registration_number || ''}
              disabled={isInputDisabled}
              type="text"
              label={
                <InfoLabel
                  text="Business registration number (EIN/Tax number/ID)*"
                  content={MouseTipBusinessReg}
                  fixedTo="left"
                  infoBoxClass={css.infoBox}
                  infoIconClass={maincss.infoIcon}
                ></InfoLabel>
              }
              boxClassName={maincss.outerBoxInputField}
              onChange={(event) => handleFormDataChange(event)}
            />
          </div>
          <div className={maincss.inputBox}>
            <Input
              name="base_sense_url"
              value={formData.base_sense_url || ''}
              disabled={isInputDisabled}
              type="text"
              label="Base Sense URL for this EIN (if multiple, separate by commas)*"
              boxClassName={maincss.outerBoxInputField}
              onChange={(event) => handleFormDataChange(event)}
            />
          </div>
          <div className={maincss.inputBox}>
            <Input
              name="duns_or_gin_or_lei"
              value={formData.duns_or_gin_or_lei || ''}
              disabled={isInputDisabled}
              type="text"
              label="DUNS or GIIN or LEI Number (optional but recommended)"
              boxClassName={maincss.outerBoxInputField}
              onChange={(event) => handleFormDataChange(event)}
            />
          </div>
          <div className={maincss.inputBox}>
            <Input
              name="company_website"
              value={formData.company_website || ''}
              disabled={isInputDisabled}
              type="text"
              label="Company Website*"
              boxClassName={maincss.outerBoxInputField}
              onChange={(event) => handleFormDataChange(event)}
            />
          </div>
          <div className={maincss.inputBox}>
            <Select
              name="region"
              value={formData.region || null}
              disabled={isInputDisabled}
              // @ts-expect-error - TS4104 - The type 'readonly [{ readonly label: "USA and Canada"; readonly value: "USA and Canada"; }, { readonly label: "Europe"; readonly value: "Europe"; }, { readonly label: "Asia"; readonly value: "Asia"; }, { ...; }, { ...; }]' is 'readonly' and cannot be assigned to the mutable type 'SelectOption[]'.
              options={selectFieldOptions['region']}
              classNames={{box: maincss.outerBoxInputField}}
              onChange={(option) => handleSelectChange('region', option)}
              label="Region of operation"
            />
          </div>
          <div className={maincss.inputBox}>
            <Input
              name="stock_symbol"
              value={formData.stock_symbol || ''}
              disabled={isInputDisabled}
              type="text"
              label="Stock Symbol"
              boxClassName={maincss.outerBoxInputField}
              onChange={(event) => handleFormDataChange(event)}
            />
          </div>
          <div className={maincss.inputBox}>
            <Input
              name="stock_exchange"
              value={formData.stock_exchange || ''}
              disabled={isInputDisabled}
              type="text"
              label="Stock Exchange"
              boxClassName={maincss.outerBoxInputField}
              onChange={(event) => handleFormDataChange(event)}
            />
          </div>
        </div>
      </Collapsible>
      <hr className={maincss.dividerLine}></hr>
      <Collapsible
        title="Enter Rep Details*"
        containerClassName={designcss.collapsibleBlock}
        headerClassName={maincss.headerText}
      >
        <div className={css.repDetails}>
          {repList.map((rep, index) => (
            <div key={index} className={css.repInfoDiv}>
              <div className={css.repHeader}>
                <Clickable
                  className={classify(maincss.textPrimary, css.repHeaderText)}
                >
                  Rep {index + 1}
                </Clickable>
                {repList.length > 1 && (
                  <Button
                    type="tertiary"
                    className={css.deleteIcon}
                    onClick={() => handleDeleteRep(index)}
                    size="small"
                  >
                    <span>&#8212;</span>
                  </Button>
                )}
              </div>
              <div className={maincss.sidePanelForm}>
                <div className={maincss.inputBox}>
                  <Input
                    name="full_name"
                    type="text"
                    label="Full Name*"
                    boxClassName={maincss.outerBoxInputField}
                    value={rep.full_name}
                    onChange={(event) => handleRepDetailChange(event, index)}
                    disabled={isInputDisabled}
                  />
                </div>
                <div className={maincss.inputBox}>
                  <Input
                    name="email"
                    type="email"
                    label="Email*"
                    boxClassName={maincss.outerBoxInputField}
                    value={rep.email}
                    disabled={isInputDisabled}
                    onChange={(event) => handleRepDetailChange(event, index)}
                  />
                </div>
                <div className={maincss.inputBox}>
                  <Input
                    name="phone_number"
                    type="text"
                    label="Phone Number*"
                    boxClassName={maincss.outerBoxInputField}
                    value={rep.phone_number}
                    disabled={isInputDisabled}
                    onChange={(event) => handleRepDetailChange(event, index)}
                  />
                </div>
                <div className={maincss.inputBox}>
                  <Input
                    name="title"
                    type="text"
                    label="Title*"
                    boxClassName={maincss.outerBoxInputField}
                    value={rep.title}
                    disabled={isInputDisabled}
                    onChange={(event) => handleRepDetailChange(event, index)}
                  />
                </div>
              </div>
            </div>
          ))}
          <Button
            type="tertiary"
            className={css.addRepBtn}
            icon={<PlusIcon />}
            onClick={handleAddRep}
            iconPosition="left"
            size="small"
            disabled={isInputDisabled || addRepDisabled}
          >
            Add Rep
          </Button>
        </div>
      </Collapsible>
      <DefaultFooter
        disableSubmit={disableSubmit}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
      />
    </div>
  );
};
